import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  CreateFeatureForm, Feature, FeatureDepartment, MultiDeptValue, getModuleScope,
} from '../../../types/feature';
import { featureService } from '../../../services/featureService';
import { usePermissionsConfig } from '../../../hooks/usePermissionsConfig';
import { toKebabCase, toCamelCase, ActionCatalogEntry } from './featureFormConstants';

export type FeatureFormMode = 'create' | 'edit';

export interface UseFeatureFormArgs {
  mode: FeatureFormMode;
  isOpen: boolean;
  defaultDepartment?: FeatureDepartment;
  feature?: Feature | null;
}

/** Parse a `module.action` permission key. Degrades gracefully when malformed. */
export function parsePermissionKey(permissionKey: string): { module: string; action: string } {
  const trimmed = (permissionKey ?? '').trim();
  if (!trimmed) return { module: '', action: 'read' };
  const dot = trimmed.indexOf('.');
  if (dot === -1) return { module: trimmed, action: 'read' };
  return { module: trimmed.slice(0, dot), action: trimmed.slice(dot + 1) || 'read' };
}

/** Error-field keys owned by each wizard step — drives per-step error clearing in validateStep. */
const STEP_ERROR_KEYS: Record<number, string[]> = {
  0: ['name', 'key', 'icon'],
  1: ['route', 'businessUnit'],
  2: ['module', 'perm'],
};

export function useFeatureForm({ mode, isOpen, defaultDepartment = 'COMMON', feature }: UseFeatureFormArgs) {
  const isEdit = mode === 'edit';

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyManual, setKeyManual] = useState(false);
  const [description, setDescription] = useState('');
  const [route, setRoute] = useState('');
  const [isGroupMenu, setIsGroupMenu] = useState(false);
  const [icon, setIcon] = useState('GridIcon');
  const [department, setDepartment] = useState<FeatureDepartment>(defaultDepartment);
  const [useMultipleDepts, setUseMultipleDepts] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<MultiDeptValue[]>([]);
  const [parentKey, setParentKey] = useState('');
  const [order, setOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  const [moduleInput, setModuleInput] = useState('');
  const [moduleManual, setModuleManual] = useState(false);
  const [primaryAction, setPrimaryAction] = useState('read');
  const [scopeActions, setScopeActions] = useState<string[]>([]);
  const [showModuleSuggest, setShowModuleSuggest] = useState(false);
  const [scopeUserEdited, setScopeUserEdited] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [parentFeatures, setParentFeatures] = useState<Feature[]>([]);

  const { data: permissionsConfig, isLoading: isPermsLoading } = usePermissionsConfig();
  const actionCatalog = useMemo<ActionCatalogEntry[]>(
    () => permissionsConfig?.actionCatalog ?? [], [permissionsConfig],
  );
  const knownModules = useMemo(() => permissionsConfig?.modules ?? [], [permissionsConfig]);

  const lockKey = isEdit;
  const lockModule = isEdit;

  const permissionKey = `${moduleInput || toCamelCase(name) || 'module'}.${primaryAction || 'read'}`;

  useEffect(() => {
    if (isEdit) return;
    if (!keyManual && name) setKey(toKebabCase(name));
  }, [name, keyManual, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    if (!moduleManual && key) setModuleInput(toCamelCase(key));
  }, [key, moduleManual, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    if (!moduleInput.trim()) return;
    const scope = getModuleScope(moduleInput.trim() + '.read');
    if (!scope) return;
    setDepartment(scope.defaultBU);
    if (scope.dataScope === 'shared' && scope.defaultBU === 'COMMON') {
      setUseMultipleDepts(false);
      setSelectedDepartments([]);
    }
  }, [moduleInput, isEdit]);

  useEffect(() => {
    if (isGroupMenu) setRoute('#');
    else if (route === '#') setRoute('');
  }, [isGroupMenu, route]);

  useEffect(() => {
    if (!isOpen) return;
    featureService.getAllFeaturesForDept(undefined)
      .then(all => setParentFeatures(all.filter(f => !f.parentKey && f.key !== feature?.key)))
      .catch(() => {});

    if (isEdit && feature) {
      const units = feature.businessUnits ?? feature.departments;
      const depts = units && units.length > 0 ? units : undefined;
      const parsed = parsePermissionKey(feature.permissionKey);
      setName(feature.name);
      setKey(feature.key);
      setKeyManual(true);
      setDescription(feature.description ?? '');
      setRoute(feature.route);
      setIsGroupMenu(feature.route === '#');
      setIcon(feature.icon);
      setDepartment((feature.businessUnit ?? feature.department) || 'COMMON');
      setUseMultipleDepts(!!depts);
      setSelectedDepartments(depts ?? []);
      setParentKey(feature.parentKey ?? '');
      setOrder(feature.order ?? 0);
      setIsActive(feature.isActive ?? true);
      setModuleInput(parsed.module);
      setModuleManual(true);
      setPrimaryAction(parsed.action);
      setScopeActions(feature.actions ?? ['read']);
      setScopeUserEdited(true);
      setErrors({});
    } else {
      setName(''); setKey(''); setKeyManual(false); setDescription(''); setRoute('');
      setIsGroupMenu(false); setIcon('GridIcon'); setDepartment(defaultDepartment);
      setUseMultipleDepts(false); setSelectedDepartments([]);
      setParentKey(''); setOrder(0); setIsActive(true);
      setModuleInput(''); setModuleManual(false); setPrimaryAction('read');
      setScopeActions([]); setScopeUserEdited(false); setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, feature, defaultDepartment]);

  useEffect(() => {
    if (!isOpen || isEdit) return;
    if (scopeUserEdited || actionCatalog.length === 0) return;
    setScopeActions(actionCatalog.map(a => a.key));
  }, [isOpen, isEdit, actionCatalog, scopeUserEdited]);

  const moduleSuggestions = useMemo(() => {
    if (!moduleInput) return [] as { key: string; name: string }[];
    const q = moduleInput.toLowerCase();
    return knownModules.filter(m => m.key.toLowerCase().includes(q) || (m.name || '').toLowerCase().includes(q));
  }, [moduleInput, knownModules]);

  const handleScopeToggle = useCallback((action: string) => {
    setScopeUserEdited(true);
    setScopeActions(prev => prev.includes(action) ? prev.filter(a => a !== action) : [...prev, action]);
  }, []);

  const validateStep = useCallback((step: number): boolean => {
    const e: Record<string, string> = {};
    if (step === 0) {
      const trimmedName = name.trim();
      if (!trimmedName || trimmedName.length < 2) e.name = 'Feature name is required (min 2 chars)';
      if (!lockKey) {
        if (!key.trim() || key.length < 2) e.key = 'Feature key is required (min 2 chars)';
        else if (!/^[a-z0-9-]+$/.test(key)) e.key = 'Key: lowercase letters, numbers, hyphens only';
      }
      if (!icon) e.icon = 'Please select an icon';
    } else if (step === 1) {
      if (!isGroupMenu && !route.trim()) e.route = 'Route is required (or check "Group Menu")';
      if (useMultipleDepts && selectedDepartments.length === 0) e.businessUnit = 'Select at least one business unit';
    } else if (step === 2) {
      if (!lockModule && !moduleInput.trim()) e.module = 'Module name is required';
      if (!primaryAction) e.perm = 'Select a primary action';
    }
    setErrors(prev => {
      // Clear only the error keys this step owns, then merge in fresh errors.
      const next = { ...prev };
      for (const k of STEP_ERROR_KEYS[step] ?? []) delete next[k];
      return { ...next, ...e };
    });
    return Object.keys(e).length === 0;
  }, [name, key, icon, route, isGroupMenu, useMultipleDepts, selectedDepartments,
      moduleInput, primaryAction, lockKey, lockModule]);

  const buildPayload = useCallback((): CreateFeatureForm => ({
    key: key.trim(),
    name: name.trim(),
    route: isGroupMenu ? '#' : route.trim(),
    icon,
    permissionKey,
    parentKey: parentKey || undefined,
    businessUnit: useMultipleDepts ? 'COMMON' : department,
    businessUnits: useMultipleDepts && selectedDepartments.length > 0 ? selectedDepartments : undefined,
    order,
    isActive,
    description: description.trim() || undefined,
    actions: scopeActions.length > 0 ? scopeActions : undefined,
  }), [key, name, isGroupMenu, route, icon, permissionKey, parentKey, useMultipleDepts,
       department, selectedDepartments, order, isActive, description, scopeActions]);

  const parentName = parentFeatures.find(f => f.key === parentKey)?.name;

  return {
    name, key, keyManual, description, route, isGroupMenu, icon, department,
    useMultipleDepts, selectedDepartments, parentKey, order, isActive,
    moduleInput, moduleManual, primaryAction, scopeActions, showModuleSuggest,
    setName, setKey, setKeyManual, setDescription, setRoute, setIsGroupMenu, setIcon,
    setDepartment, setUseMultipleDepts, setSelectedDepartments, setParentKey, setOrder,
    setIsActive, setModuleInput, setModuleManual, setPrimaryAction, setShowModuleSuggest,
    handleScopeToggle,
    permissionKey, parentFeatures, parentName, moduleSuggestions,
    actionCatalog, isPermsLoading, isEdit, lockKey, lockModule,
    errors, setErrors, validateStep, buildPayload,
  };
}

export type FeatureFormApi = ReturnType<typeof useFeatureForm>;
